import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, HAT_RIBBON, hatAt, type Part, RIBBON } from '../body';
import type { Hat } from '../headwear';
import { FACE } from '../looks';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';

/**
 * The beekeeper set (ADR-0112), drawn for the game's zoom, where a gardener is 64×72 art px and the hat's shape is what
 * reads: a white canvas dome on a wide brim with a black net veil hanging to the shoulders (the head becomes a lantern),
 * a puffy white jumpsuit in the team's wellies, and guns in honey.
 *
 * - The veil is the hat's `head` part, so it moves with the face (the hat lags it a frame; its top hides under the
 *   brim whatever the lag). Over the face only its threads are drawn and the face shows through; elsewhere the net
 *   shows the dark inside. Knocked off, the veil goes with the hat: it hangs under it as it tumbles and lies crumpled
 *   out from under it on the grass. The team: the band, the ribbon, the wellies and the zip; with the hat off the
 *   wellies carry it.
 * - Rubric (`looks` sheets and the set on six looks × three teams at 3×, 1:1 and box-filtered to 720p, the wardrobe
 *   tiles and the stage, a 2v2 at 1280×720 with a knock-out): hat S5 R4 P4 L4 C4 A4 F4, suit S4 R4 P5 L4 C4 A4 F4,
 *   Honey R4 P5 F4. Still weak: blown up (3×) the net cages the face, at 1:1 it is a fine net and at 720p a lattice;
 *   glasses, shades and earrings are drawn over the veil (a look's extras come after a hat's head parts); long hair, an
 *   afro or locs hang out of the veil's back; the white's lit rim is slate `3`, close to the lawn's value (the white
 *   carries the shape); tumbling with its veil the hat reads a little like a lampshade; on an all-steel gun Honey sits
 *   between Ember and Gold (a darker amber), its chocolate wood sets it apart on the rest.
 */

// --- The hat -------------------------------------------------------------------------------------------------------

/** The brim, an ellipse seen from above (hat px, pixel centres), and the crown's dome standing on it. */
const BRIM = { cx: 32, cy: 14.5, rx: 27.5, ry: 7.5 } as const;
const CROWN = { cx: 32, base: 13, rx: 13.5, ry: 3.5, h: 13 } as const;
const HAT_H = 25;

const inBrim = (x: number, y: number) =>
  ((x + 0.5 - BRIM.cx) / BRIM.rx) ** 2 + ((y + 0.5 - BRIM.cy) / BRIM.ry) ** 2 <= 1;

/** The crown's front bottom (its base ellipse's near half) at column x. */
const crownBase = (x: number) => {
  const dx = (x + 0.5 - CROWN.cx) / CROWN.rx;
  return CROWN.base + CROWN.ry * Math.sqrt(Math.max(0, 1 - dx * dx));
};
function inCrown(x: number, y: number): boolean {
  const dx = (x + 0.5 - CROWN.cx) / CROWN.rx;
  if (Math.abs(dx) > 1) return false;
  const s = Math.sqrt(1 - dx * dx);
  return y + 0.5 >= CROWN.base - CROWN.h * s && y + 0.5 <= CROWN.base + CROWN.ry * s;
}

/**
 * The beekeeper's hat as worn (64 × `HAT_H` from `HAT_Y`): a white canvas dome on a wide flat brim, lit from the
 * top-left, the team's band round the dome's foot, the crown's shadow on the brim to its right, the brim's lip under
 * its front edge.
 */
function beeHat(): PixelCanvas {
  const c = new PixelCanvas(64, HAT_H);
  for (let y = 0; y < HAT_H; y++) {
    for (let x = 0; x < 64; x++) {
      if (inBrim(x, y)) {
        const dx = (x + 0.5 - BRIM.cx) / BRIM.rx;
        const dy = (y + 0.5 - BRIM.cy) / BRIM.ry;
        const r = dx * dx + dy * dy;
        let tone: PaletteKey = 'w';
        // The crown's shadow falls on the brim to its right.
        if (x > CROWN.cx + 4 && x < CROWN.cx + CROWN.rx + 8 && y > CROWN.base - 2 && y < CROWN.base + 6) tone = '5';
        if (r > 0.82) tone = dx < -0.2 || dy < 0 ? '5' : '4';
        c.set(x, y, tone);
      } else if (inBrim(x, y - 1) || inBrim(x, y - 2)) {
        // The lip: the brim's edge under its front.
        c.set(x, y, inBrim(x, y - 1) ? '5' : '4');
      }
    }
  }
  for (let y = 0; y < HAT_H; y++) {
    for (let x = 0; x < 64; x++) {
      if (!inCrown(x, y)) continue;
      const dx = (x + 0.5 - CROWN.cx) / CROWN.rx;
      const base = crownBase(x);
      const up = Math.min(0, (y + 0.5 - CROWN.base) / CROWN.h);
      const nz = Math.sqrt(Math.max(0, 1 - dx * dx - up * up));
      const lit = -0.55 * dx - 0.55 * up + 0.6 * nz;
      let tone: PaletteKey = lit > 0.45 ? 'w' : lit > 0.1 ? '5' : lit > -0.2 ? '4' : '3';
      // The team's band round the dome's foot: lit top row, shaded on the right.
      if (y + 0.5 > base - 4) tone = y + 0.5 <= base - 3 ? 'c' : y + 0.5 > base - 1 || dx > 0.55 ? 'B' : 'b';
      c.set(x, y, tone);
    }
  }
  return c;
}

// --- The veil ------------------------------------------------------------------------------------------------------

/**
 * A thread of the mesh at (x, y) (frame px): a square net, a thread every third px (a checker's dither read as a grey
 * face once the world is drawn under 1:1; a net still reads as a net). Its lines miss the eyes' highlights.
 */
const thread = (x: number, y: number) => x % 3 === 0 || y % 3 === 0;

/** The face's pixels (frame px): through the mesh, the face shows there. */
const faceAt = (x: number, y: number) => FACE[0].get(x - FACE[1], y - FACE[2]) !== null;

/**
 * A veil hanging from the brim, `h` rows from its top (hidden under the brim) to its hem's front: soft folds that
 * billow a little, then gathered in at the hem; its threads lit on the left, a dark inside showing between them (`see`
 * answers where something shows through instead, in the veil's own px), the hem a white band. Columns are frame px
 * (the veil is centred on the hat).
 */
function drape(h: number, top: number, see: (x: number, y: number) => boolean): PixelCanvas {
  const c = new PixelCanvas(64, h);
  for (let y = 0; y < h; y++) {
    const t = y / (h - 1);
    // Half its width: billowing to the middle, gathered in at the hem.
    const half = 23.5 + Math.sin(Math.PI * Math.min(1, t / 0.8)) - (t > 0.75 ? ((t - 0.75) / 0.25) * 4.5 : 0);
    for (let x = 0; x < 64; x++) {
      const u = (x + 0.5 - BRIM.cx) / half;
      if (Math.abs(u) > 1) continue;
      // The hem: its front lowest, rising to the sides.
      const hem = h - 3 * (1 - Math.sqrt(1 - u * u));
      if (y + 0.5 > hem) continue;
      if (y + 0.5 > hem - 2) {
        c.set(x, y, y + 0.5 > hem - 1 || x % 3 === 0 || u > 0.6 ? '5' : 'w');
        continue;
      }
      // Folds: a crest catches the light, the trough beside it is in shadow; the left side faces the light.
      const fold = ((((u + 1) * 4.5) % 1) + 1) % 1;
      const light = -u * 0.6 + (fold < 0.25 ? 0.5 : fold > 0.55 && fold < 0.8 ? -0.5 : 0);
      const on = thread(x, y + top);
      if (see(x, y + top)) {
        if (on) c.set(x, y, '1');
      } else c.set(x, y, !on ? '1' : light > 0.35 ? '3' : light < -0.45 ? '1' : '2');
    }
  }
  return c;
}

/** The veil worn: from under the brim (whatever the hat's lag) to the shoulders; the face shows through it. */
const VEIL_TOP = 20;
const veil = (): Part => [drape(29, VEIL_TOP, faceAt), 0, VEIL_TOP];

// --- Knocked off ---------------------------------------------------------------------------------------------------

/**
 * The veil off the head (nobody in it), hanging from under the brim: `h` rows from the brim's middle, gathered in to
 * a bag, its folds running down into the hem, which `wave`s a little.
 */
function bag(h: number, wave = 0): PixelCanvas {
  const c = new PixelCanvas(64, h);
  for (let y = 0; y < h; y++) {
    const t = y / (h - 1);
    const half = 21 - 9 * t;
    for (let x = 0; x < 64; x++) {
      const u = (x + 0.5 - BRIM.cx) / half;
      if (Math.abs(u) > 1) continue;
      const hem = h - 1.5 * (1 - Math.sqrt(1 - u * u)) + wave * Math.sin(u * 5);
      if (y + 0.5 > hem) continue;
      if (y + 0.5 > hem - 2) {
        c.set(x, y, y + 0.5 > hem - 1 || x % 3 === 0 || u > 0.6 ? '5' : 'w');
        continue;
      }
      const fold = ((((u + 1) * 3.5) % 1) + 1) % 1;
      const light = -u * 0.6 + (fold < 0.25 ? 0.5 : fold > 0.55 && fold < 0.8 ? -0.5 : 0);
      if (thread(x, y)) c.set(x, y, light > 0.35 ? '3' : light < -0.45 ? '1' : '2');
      else c.set(x, y, light > 0.55 ? '2' : '1');
    }
  }
  return c;
}

const BAG_H = 19;

/** The hat (`beeHat`) with its veil hanging under it. */
const hatWithVeil = (hat: PixelCanvas, wave: number) =>
  new PixelCanvas(64, Math.round(BRIM.cy) + BAG_H).blit(bag(BAG_H, wave), 0, Math.round(BRIM.cy)).blit(hat, 0, 0);

/** Upside down: the brim's underside round the dark hollow of the crown, the veil fallen round the dome. */
function upsideDown(): PixelCanvas {
  const top = Math.round(BRIM.cy);
  const c = new PixelCanvas(64, top + BAG_H).blit(bag(BAG_H, -1), 0, top);
  for (let y = 0; y < HAT_H; y++) {
    for (let x = 0; x < 64; x++) {
      if (!inBrim(x, y)) continue;
      const dx = (x + 0.5 - BRIM.cx) / CROWN.rx;
      const dy = (y + 0.5 - BRIM.cy) / (CROWN.ry + 1);
      const hollow = dx * dx + dy * dy;
      const r = ((x + 0.5 - BRIM.cx) / BRIM.rx) ** 2 + ((y + 0.5 - BRIM.cy) / BRIM.ry) ** 2;
      let tone: PaletteKey = r > 0.8 ? (dy < 0 ? 'w' : '4') : '5';
      if (hollow <= 1) tone = dy < -0.4 ? '3' : dx > 0.5 ? '2' : '1';
      c.set(x, y, tone);
    }
  }
  return c;
}

/** On the grass: the hat (`beeHat`) on its brim, the veil crumpled out from under it, its hem's arc, the ribbon trailing. */
function lying(hat: PixelCanvas): PixelCanvas {
  const c = new PixelCanvas(64, 40);
  const heap: readonly [number, number, number, number][] = [
    [22, 31, 19, 5],
    [40, 33, 13, 3.5],
    [8, 27, 6, 4],
  ];
  const inHeap = (x: number, y: number) =>
    heap.some(([cx, cy, rx, ry]) => ((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / ry) ** 2 <= 1);
  for (let y = 0; y < 40; y++) {
    for (let x = 0; x < 64; x++) {
      if (!inHeap(x, y)) continue;
      const u = (x + 0.5 - 24) / 22;
      const fold = ((((u + 1) * 4.5) % 1) + 1) % 1;
      if (!inHeap(x, y + 1) || !inHeap(x - 1, y)) c.set(x, y, !inHeap(x, y + 1) && x % 3 === 0 ? '5' : 'w');
      else if (!inHeap(x, y + 2)) c.set(x, y, '5');
      else if (thread(x, y)) c.set(x, y, fold < 0.25 ? '3' : u > 0.5 ? '1' : '2');
      else c.set(x, y, '1');
    }
  }
  return c.blit(HAT_RIBBON, 46, 27).blit(hat, 3, 1);
}

/**
 * `src` (the hat with its veil, its top at the hat's) tipped by `deg` on a 64×40 canvas round the brim's middle, by
 * three shears like body.ts' `tipped`, which places a shorter hat lower.
 */
function tilt(deg: number, src: PixelCanvas): PixelCanvas {
  const out = new PixelCanvas(64, 40);
  const a = Math.tan((deg * Math.PI) / 360);
  const b = -Math.sin((deg * Math.PI) / 180);
  const [cx, cy] = [32, 19];
  for (let y = 0; y < src.h; y++) {
    for (let x = 0; x < src.w; x++) {
      const p = src.get(x, y);
      if (p === null) continue;
      let dx = x - cx;
      let dy = y + 1 - cy;
      dx -= Math.round(a * dy);
      dy += Math.round(b * dx);
      dx -= Math.round(a * dy);
      out.set(cx + dx, cy + dy, p);
    }
  }
  const n4 = (x: number, y: number) =>
    [out.get(x - 1, y), out.get(x + 1, y), out.get(x, y - 1), out.get(x, y + 1)].filter((q) => q !== null);
  for (let y = 0; y < out.h; y++) {
    for (let x = 0; x < out.w; x++) {
      const near = n4(x, y);
      if (out.get(x, y) !== null && near.length <= 1) out.set(x, y, null);
      else if (out.get(x, y) === null && near.length >= 3) out.set(x, y, near[1]!);
    }
  }
  return out;
}

const BEEKEEPER_HAT = (): Hat => {
  const hat = beeHat();
  return {
    worn: false,
    hat: { flat: hat, up: hat, down: hat },
    ribbon: RIBBON,
    head: [veil()],
    fly: [
      hatAt(1, hatWithVeil(hat, 0)),
      tilt(14, hatWithVeil(hat, 1)),
      hatAt(1, upsideDown()),
      tilt(-14, hatWithVeil(hat, -1)),
    ],
    rest: [lying(hat)],
  };
};

// --- The suit ------------------------------------------------------------------------------------------------------

/** The suit's rows (frame x from, to) from y 44: rounded padded shoulders, gathered in at the waist. */
const SUIT_ROWS: readonly (readonly [number, number])[] = [
  [15, 44],
  [12, 47],
  [10, 49],
  [9, 50],
  [9, 50],
  [9, 50],
  [10, 49],
  [10, 49],
  [11, 48],
  [11, 48],
  [12, 47],
  [12, 47],
];
const ZIP_X = 31;

/** The chest pockets (frame x from, to; rows from, to of the suit). */
const POCKETS = [
  [13, 22, 3, 8],
  [35, 43, 3, 8],
] as const;

/**
 * A puffy white jumpsuit (from y 44): padded shoulders lit from the top-left, the right side and the waist in shade, a
 * stand-up collar under the chin (where a hood would zip on), the team's zip down the front (its pull at the collar, the
 * placket's shadow beside it), two big patch pockets with a flap and the flap's shadow, the waist gathered.
 */
function suit(): Part {
  const c = new PixelCanvas(42, SUIT_ROWS.length);
  const x0 = 9;
  const last = SUIT_ROWS.length - 1;
  for (const [y, [a, b]] of SUIT_ROWS.entries()) {
    for (let x = a; x <= b; x++) {
      const shade = x >= b - 1 ? 2 : x >= b - 5 ? 1 : 0;
      let tone: PaletteKey = shade === 2 ? '4' : shade === 1 || y === last ? '5' : 'w';
      // The collar under the chin: its shadow, then its lit roll.
      if (y <= 1 && x >= 24 && x <= 43) tone = y === 0 ? '5' : x > 40 ? '5' : 'w';
      for (const [l, r, t, bt] of POCKETS) {
        if (x < l || x > r || y < t || y > bt) continue;
        // The flap's edge and its shadow on the pocket, the pocket's seams.
        const edge = y === t + 2 ? (shade ? '4' : '5') : x === l || x === r || y === bt || y === t ? '5' : null;
        if (edge) tone = shade === 2 ? '4' : edge;
      }
      // The gathered waist.
      if (y === last - 1) tone = x % 2 ? (shade === 2 ? '4' : '5') : tone;
      // The team's zip, its pull at the collar, the placket's shadow to its right.
      if (x === ZIP_X && y >= 1) tone = y <= 2 ? 'c' : y === last ? 'B' : 'b';
      if (x === ZIP_X + 1 && y >= 2 && y < last) tone = '5';
      c.set(x - x0, y, tone);
    }
  }
  return [c, x0, 44];
}

// The suit lying: the gathered waist (left), the zip along the body, a sleeve either side.
const DEAD_SUIT = grid(`
  ....wwww
  ...wwwww
  5wwwwww5
  wwwwwwww
  5wwwwwww
  wwwwwwww
  5wwwwwww
  wwwwwwww
  5bbbbbbc
  wwwwwwww
  5wwwwwww
  wwwwwwww
  5wwwwww5
  45555555
  455555w.
  45555555
  44445555
  ....4444
`);

const BEEKEEPER_SUIT = (): Outfit => ({
  torso: suit(),
  dead: [DEAD_SUIT, 24, 51],
  // White trousers tucked into the team's wellies.
  legs: { c: 'w', b: 'w', B: '5', n: 'b', N: 'B', m: 'b' },
});

// --- The gun skin ---------------------------------------------------------------------------------------------------

/**
 * Honey (skins.ts' rules): the steel amber (rust shadows, burnt orange, a gold glint), the wood a dark chocolate, the
 * brass left golden; the darkest steps stay dark for the outline. Ember is red-hot and Gold bright yellow: Honey is a
 * step darker and browner than Gold, and two-tone wherever a gun has wood.
 */
const HONEY = {
  k: 'N',
  '1': 'N',
  '2': 'n',
  '3': 'u',
  '4': 'U',
  '5': 'Y',
  N: 'k',
  n: 'N',
  m: 'N',
  t: 'n',
  e: 'm',
} as const;

/** The beekeeper set (ADR-0112): a white hat with a black veil, a white suit in the team's wellies, honey guns. */
export const BEEKEEPER = drawnLater({ head: BEEKEEPER_HAT, outfit: BEEKEEPER_SUIT, gun: HONEY });
